"use client"

import { Control, Controller } from "react-hook-form"
import { FormSection } from "@/components/ui/form-section"
import { CheckboxList } from "@/components/ui/checkbox-list"
import { Checkbox } from "@/components/ui/checkbox"
import { Transformation } from "@/lib/types/transformations"
import { SettingsResponse } from "@/lib/types/api"
import { useI18n } from "@/lib/hooks/use-i18n"
import { getLocalizedField } from "@/lib/i18n/translations"

interface CreateSourceFormData {
  type: 'link' | 'upload' | 'text'
  title?: string
  url?: string
  content?: string
  file?: FileList | File
  notebooks?: string[]
  transformations?: string[]
  embed: boolean
  build_graph: boolean
  async_processing: boolean
  enable_ocr: boolean
}

interface ProcessingStepProps {
  control: Control<CreateSourceFormData>
  transformations: Transformation[]
  selectedTransformations: string[]
  onToggleTransformation: (transformationId: string) => void
  loading?: boolean
  settings?: SettingsResponse
}

export function ProcessingStep({
  control,
  transformations,
  selectedTransformations,
  onToggleTransformation,
  loading = false,
  settings
}: ProcessingStepProps) {
  const { t, language } = useI18n()
  const transformationItems = transformations.map((transformation) => ({
    id: transformation.id,
    title: getLocalizedField(transformation, 'title', language),
    description: getLocalizedField(transformation, 'description', language)
  }))

  return (
    <div className="space-y-8">
      <FormSection
        title={t('addSourceDialog.step3.transformations.title')}
        description={t('addSourceDialog.step3.transformations.description')}
      >
        <CheckboxList
          items={transformationItems}
          selectedIds={selectedTransformations}
          onToggle={onToggleTransformation}
          loading={loading}
          emptyMessage={t('addSourceDialog.step3.transformations.empty')}
        />
      </FormSection>

      <FormSection
        title={t('addSourceDialog.step3.processing.title')}
        description={t('addSourceDialog.step3.processing.description')}
      >
        <div className="space-y-4">
          {/* Vector RAG Toggle */}
          {settings?.default_embedding_option === 'ask' && (
            <Controller
              control={control}
              name="embed"
              render={({ field }) => (
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md hover:bg-muted">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium block">{t('addSourceDialog.step3.processing.embed.label')}</span>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('addSourceDialog.step3.processing.embed.description')}
                    </p>
                  </div>
                </label>
              )}
            />
          )}

          {settings?.default_embedding_option === 'always' && (
            <div className="p-3 rounded-md bg-primary/10 border border-primary/30">
              <div className="flex items-start gap-3">
                <div className="w-4 h-4 bg-primary rounded-full mt-0.5 flex-shrink-0"></div>
                <div className="flex-1">
                  <span className="text-sm font-medium block text-primary">{t('addSourceDialog.step3.processing.embed.auto.label')}</span>
                  <p className="text-xs text-primary mt-1">
                    {t('addSourceDialog.step3.processing.embed.auto.description', { settingsLink: t('navigation.item.settings') })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {settings?.default_embedding_option === 'never' && (
            <div className="p-3 rounded-md bg-muted border border-border">
              <div className="flex items-start gap-3">
                <div className="w-4 h-4 bg-muted-foreground rounded-full mt-0.5 flex-shrink-0"></div>
                <div className="flex-1">
                  <span className="text-sm font-medium block text-foreground">{t('addSourceDialog.step3.processing.embed.disabled.label')}</span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('addSourceDialog.step3.processing.embed.disabled.description', { settingsLink: t('navigation.item.settings') })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* GraphRAG Toggle */}
          <Controller
            control={control}
            name="build_graph"
            render={({ field }) => (
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md hover:bg-muted">
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium block">{t('addSourceDialog.step3.processing.graphrag.label')}</span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('addSourceDialog.step3.processing.graphrag.description')}
                  </p>
                </div>
              </label>
            )}
          />

          {/* OCR Toggle */}
          <Controller
            control={control}
            name="enable_ocr"
            render={({ field }) => (
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md hover:bg-muted">
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium block">{t('notebooks.chat.ocr.label')}</span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('notebooks.chat.ocr.tooltip')}
                  </p>
                </div>
              </label>
            )}
          />
        </div>
      </FormSection>
    </div>
  )
}
